import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { CatalogEnvelope, CatalogListEnvelope } from '../types/catalog-service.api.types';

/**
 * Unwraps a single-object envelope's `data`, throwing when the wire response omits
 * it (optional/nullable per the generated `ResponseSchema_*_` types). A 200 with no
 * `data` is a contract violation the caller should fail loudly on, not propagate as
 * a silently-`null` value typed as the full object.
 */
export function unwrap<T>(body: CatalogEnvelope<T>): T {
  if (body.data === null || body.data === undefined) {
    throw new Error('catalog-service response envelope carried no data');
  }
  return body.data;
}

/**
 * Normalises a catalog list envelope into a UI page, mapping each row. Services
 * call this so hooks/components never see the wire `pagination` block. `data` is
 * optional on the wire envelope, so a response that omits it yields an empty page
 * instead of throwing on `.map`.
 */
export function toPage<TWire, TUi>(
  body: CatalogListEnvelope<TWire>,
  mapRow: (row: TWire) => TUi,
): CatalogPage<TUi> {
  return {
    items: (body.data ?? []).map(mapRow),
    total: body.pagination.total,
    page: body.pagination.page,
    size: body.pagination.size,
    totalPages: body.pagination.totalPages,
    nextCursor: body.pagination.nextCursor ?? null,
  };
}

/**
 * Drops `undefined`, `null` and empty-string values so they are not sent as
 * query params (catalog-service 422s some empty values and treats absent as "any").
 */
export function toSearchParams(
  params: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

/** One query value: a scalar, or a list sent as a repeated key. */
type QueryValue = string | number | boolean | null | undefined;

/**
 * `toSearchParams` for reads with repeatable filters (`createdBy=a&createdBy=b`): scalars are
 * dropped when empty exactly as there, and each array element becomes its own key (an empty
 * array sends nothing). Returns `URLSearchParams` since a plain record cannot repeat a key.
 */
export function toRepeatedSearchParams(
  params: Record<string, QueryValue | readonly QueryValue[]>,
): URLSearchParams {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    const values: readonly QueryValue[] = Array.isArray(value) ? value : [value as QueryValue];
    for (const item of values) {
      if (item === undefined || item === null || item === '') continue;
      out.append(key, String(item));
    }
  }
  return out;
}
